/**
 * Loads the HubSpot tracking script from branding / env placeholders.
 */

import * as React from 'react';

declare global {
  interface Window {
    _hsq?: unknown[][];
    hubspot_web_interactives_running?: boolean;
  }
}

/** DOM id of the HubSpot tracking-script loader. */
const HUBSPOT_SCRIPT_ID = 'hs-script-loader';
/** DOM id of the HubSpot web-interactives embed script. */
const HUBSPOT_WEB_INTERACTIVES_ID = 'hubspot-web-interactives-loader';
/** EU1 URL for the HubSpot web-interactives embed. */
const HUBSPOT_WEB_INTERACTIVES_URL = 'https://js-eu1.hubspot.com/web-interactives-embed.js';

/** Selectors for HubSpot scripts injected into the page. */
const HUBSPOT_INJECTED_SCRIPT_SELECTORS = [
  `#${HUBSPOT_SCRIPT_ID}`,
  `#${HUBSPOT_WEB_INTERACTIVES_ID}`,
  'script[id^="hs-"]',
  'script[id^="LeadFlows-"]',
  'script[id^="CollectedForms-"]',
  'script[id^="cookieBanner-"]',
].join(', ');

/** Selectors for HubSpot overlays, banners, and cookie widgets. */
const HUBSPOT_DOM_SELECTORS = [
  '.leadinModal',
  '.leadinModal-overlay',
  '[id^="hs-overlay-cta-"]',
  '[id^="hs-web-interactive"]',
  '#hs-web-interactives-top-push-anchor',
  '#hs-eu-cookie-confirmation',
  '#hubspot-messages-iframe-container',
].join(', ');

/** Prevents Vite/esbuild from constant-folding Docker #{VAR}# placeholders at build time. */
function readRuntimeEnv(value: string | undefined): string {
  const holder: { current?: string } = {};
  holder.current = value;
  return holder.current ?? '';
}

/** True when HubSpot is enabled and a portal id is present at runtime. */
function isHubSpotConfigured(): boolean {
  const enabled = readRuntimeEnv(import.meta.env.VITE_HUBSPOT_ENABLED);
  const portalId = readRuntimeEnv(import.meta.env.VITE_HUBSPOT_PORTAL_ID).trim();
  return enabled === 'true' && portalId.length > 0;
}

/** Push the current SPA path as a HubSpot page view. */
function notifyHubSpotPageView(): void {
  window._hsq = window._hsq ?? [];
  window._hsq.push(['setPath', window.location.pathname], ['trackPageView']);
}

/** Reload the web-interactives embed so banner CTAs re-evaluate targeting. */
function refreshHubSpotWebInteractives(): void {
  window.hubspot_web_interactives_running = false;
  document.getElementById(HUBSPOT_WEB_INTERACTIVES_ID)?.remove();

  const script = document.createElement('script');
  script.id = HUBSPOT_WEB_INTERACTIVES_ID;
  script.async = true;
  script.src = HUBSPOT_WEB_INTERACTIVES_URL;
  document.body.appendChild(script);
}

/** Inject the HubSpot tracking script, or refresh interactives if already loaded. */
function injectHubSpotScript(portalId: string): void {
  if (document.getElementById(HUBSPOT_SCRIPT_ID)) {
    notifyHubSpotPageView();
    refreshHubSpotWebInteractives();
    return;
  }

  const script = document.createElement('script');
  script.id = HUBSPOT_SCRIPT_ID;
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  script.src = `https://js-eu1.hs-scripts.com/${portalId}.js`;
  script.onload = () => {
    notifyHubSpotPageView();
  };
  document.body.appendChild(script);
}

/** Remove HubSpot scripts and overlay nodes from the document. */
function removeHubSpotFromDom(): void {
  window.hubspot_web_interactives_running = false;
  document.querySelectorAll(HUBSPOT_INJECTED_SCRIPT_SELECTORS).forEach((node) => {
    node.remove();
  });
  document.querySelectorAll(HUBSPOT_DOM_SELECTORS).forEach((node) => {
    node.remove();
  });
}

/**
 * Loads the HubSpot EU1 tracking script while `enabled` is true (login page only).
 * Refreshes web-interactives on re-enter so banner CTAs re-evaluate targeting in the SPA.
 * Requires VITE_HUBSPOT_ENABLED=true and VITE_HUBSPOT_PORTAL_ID.
 */
export function useHubSpotScript(enabled: boolean): void {
  React.useEffect(() => {
    if (!enabled || !isHubSpotConfigured()) {
      return;
    }

    const portalId = readRuntimeEnv(import.meta.env.VITE_HUBSPOT_PORTAL_ID).trim();
    injectHubSpotScript(portalId);

    return () => {
      removeHubSpotFromDom();
    };
  }, [enabled]);
}
